import React, { useEffect, useState } from "react";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Save,
  Send,
  ShieldCheck,
  X,
  XCircle,
} from "lucide-react";
import { downloadFile } from "@/lib/downloadFile";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const moneyFmt = (n, currency) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

// ── GenerateDialog — exported for use in BidBoqEditor ────────────────────────

export function GenerateDialog({ open, onOpenChange, bid, onGenerated }) {
  const [valid, setValid] = useState("");
  const [pay, setPay] = useState(
    "30% advance, 60% against delivery, 10% after FAT/SAT",
  );
  const [delivery, setDelivery] = useState("Within 8 weeks from PO");
  const [warranty, setWarranty] = useState(
    "12 months from commissioning, extendable AMC",
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const q = await v2Api.quotations.generate({
        bid_id: bid.id,
        valid_until: valid || null,
        payment_terms: pay,
        delivery_terms: delivery,
        warranty_terms: warranty,
        notes: notes || null,
      });
      toast.success(`Quotation ${q.quotation_no} generated`);
      onOpenChange(false);
      onGenerated();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ?? err?.message ?? "Could not generate";
      toast.error(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Generate quotation</DialogTitle>
          <DialogDescription>
            Builds a versioned, branded quotation from the saved BOQ. GST split
            is computed from the workspace state vs. the buyer state.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Valid until</Label>
            <Input
              type="date"
              value={valid}
              onChange={(e) => setValid(e.target.value)}
            />
          </div>
          <div>
            <Label>Payment terms</Label>
            <Input value={pay} onChange={(e) => setPay(e.target.value)} />
          </div>
          <div>
            <Label>Delivery terms</Label>
            <Input
              value={delivery}
              onChange={(e) => setDelivery(e.target.value)}
            />
          </div>
          <div>
            <Label>Warranty terms</Label>
            <Input
              value={warranty}
              onChange={(e) => setWarranty(e.target.value)}
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Generating…" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Status helpers ────────────────────────────────────────────────────────────

export const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600",
  pending_approval: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  sent: "bg-blue-100 text-blue-700",
  rejected: "bg-rose-100 text-rose-700",
  withdrawn: "bg-slate-100 text-slate-500",
  expired: "bg-slate-100 text-slate-500",
};

const STATUS_LABELS = {
  draft: "Draft",
  pending_approval: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
  sent: "Sent to Buyer",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

// Linear progression steps (rejected/withdrawn are side-branches)
const STATUS_STEPS = ["draft", "pending_approval", "approved", "sent"];

const STATUS_ICONS = {
  draft: FileText,
  pending_approval: Clock,
  approved: CheckCircle2,
  sent: Send,
  rejected: XCircle,
  withdrawn: X,
  expired: XCircle,
};

// ── QuotationEditorDialog ─────────────────────────────────────────────────────

function QuotationEditorDialog({ quotation: initial, onClose, onUpdated }) {
  const [q, setQ] = useState(initial);
  const [form, setForm] = useState({
    valid_until: initial?.valid_until || "",
    payment_terms: initial?.payment_terms || "",
    delivery_terms: initial?.delivery_terms || "",
    warranty_terms: initial?.warranty_terms || "",
    notes: initial?.notes || "",
    other_charges: initial?.other_charges ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // "sent" | "withdrawn" | "reviewing"

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await v2Api.quotations.update(q.id, {
        valid_until: form.valid_until || null,
        payment_terms: form.payment_terms || null,
        delivery_terms: form.delivery_terms || null,
        warranty_terms: form.warranty_terms || null,
        notes: form.notes || null,
        other_charges: Number(form.other_charges) || 0,
      });
      setQ(updated);
      onUpdated(updated);
      toast.success("Quotation saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendForReview = async () => {
    setBusy("reviewing");
    try {
      const updated = await v2Api.quotations.sendForReview(q.id);
      setQ(updated);
      onUpdated(updated);
      toast.success("Sent for review — approval chain initiated");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Could not send for review",
      );
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (status) => {
    setBusy(status);
    try {
      const updated = await v2Api.quotations.setStatus(q.id, status);
      setQ(updated);
      onUpdated(updated);
      toast.success(`Quotation marked as ${status.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Status update failed",
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col gap-0 p-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
            {q.quotation_no}
            <span className="text-slate-400 font-normal text-sm">v{q.version}</span>
            <Badge
              className={`ml-1 text-[10px] border-0 ${STATUS_CLS[q.status] || "bg-slate-100 text-slate-600"}`}
            >
              {q.status.replace(/_/g, " ")}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-4 space-y-5 flex-1">
          {/* Amounts summary */}
          <div className="grid grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg text-center">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Subtotal</p>
              <p className="font-mono font-semibold text-sm">{moneyFmt(q.subtotal, q.currency)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">GST</p>
              <p className="font-mono font-semibold text-sm">
                {moneyFmt(q.cgst_total + q.sgst_total + q.igst_total, q.currency)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Other charges</p>
              <p className="font-mono font-semibold text-sm">{moneyFmt(q.other_charges, q.currency)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Grand total</p>
              <p className="font-mono font-bold text-blue-700">{moneyFmt(q.grand_total, q.currency)}</p>
            </div>
          </div>

          {/* Editable terms */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Terms &amp; Conditions
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valid until</Label>
                <Input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setField("valid_until", e.target.value)}
                  className="h-8 text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Other charges (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.other_charges}
                  onChange={(e) => setField("other_charges", e.target.value)}
                  className="h-8 text-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Payment terms</Label>
              <Input
                value={form.payment_terms}
                onChange={(e) => setField("payment_terms", e.target.value)}
                className="h-8 text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Delivery terms</Label>
              <Input
                value={form.delivery_terms}
                onChange={(e) => setField("delivery_terms", e.target.value)}
                className="h-8 text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Warranty terms</Label>
              <Input
                value={form.warranty_terms}
                onChange={(e) => setField("warranty_terms", e.target.value)}
                className="h-8 text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                className="text-sm mt-1 resize-none"
              />
            </div>
          </div>

          {/* Line items table — read-only snapshot */}
          {q.lines?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                Line Items
                <span className="ml-1.5 font-normal normal-case text-slate-400">
                  (snapshot — edit pricing in Bid BoQ)
                </span>
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="text-xs w-full" style={{ minWidth: 680 }}>
                  <thead className="bg-slate-50">
                    <tr className="text-[10px] uppercase text-slate-500 border-b">
                      <th className="text-left px-3 py-2 w-8">SL</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-right px-3 py-2 w-16">Qty</th>
                      <th className="text-left px-3 py-2 w-14">UoM</th>
                      <th className="text-right px-3 py-2 w-24">Rate</th>
                      <th className="text-right px-3 py-2 w-14">Disc%</th>
                      <th className="text-right px-3 py-2 w-14">GST%</th>
                      <th className="text-right px-3 py-2 w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {q.lines.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-400">{l.sl_no}</td>
                        <td className="px-3 py-2">{l.description}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.qty}</td>
                        <td className="px-3 py-2 text-slate-500">{l.uom}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {moneyFmt(l.unit_rate, q.currency)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.discount_pct}%</td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.gst_rate_pct}%</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {moneyFmt(l.line_total, q.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <DialogFooter className="px-6 py-4 border-t flex flex-wrap gap-2 sm:justify-between">
          {/* Downloads */}
          <div className="flex gap-2 flex-wrap">
            {q.pdf_doc_path && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadFile(
                    v2Api.quotations.downloadUrl(q.id, "pdf"),
                    `${q.quotation_no}.pdf`,
                  ).catch(() => toast.error("Download failed"))
                }
              >
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            )}
            {q.generated_doc_path && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadFile(
                    v2Api.quotations.downloadUrl(q.id, "docx"),
                    `${q.quotation_no}.docx`,
                  ).catch(() => toast.error("Download failed"))
                }
              >
                <Download className="w-3.5 h-3.5 mr-1" /> DOCX
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {["draft", "rejected"].includes(q.status) && (
              <Button
                size="sm"
                variant="secondary"
                disabled={!!busy}
                onClick={sendForReview}
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                {busy === "reviewing" ? "Sending…" : "Send for Review"}
              </Button>
            )}
            {["draft", "approved"].includes(q.status) && (
              <Button
                size="sm"
                disabled={!!busy}
                onClick={() => setStatus("sent")}
              >
                <Send className="w-3.5 h-3.5 mr-1" />
                {busy === "sent" ? "Marking…" : "Mark as Sent"}
              </Button>
            )}
            {q.status === "sent" && (
              <Button
                size="sm"
                variant="outline"
                className="text-slate-600"
                disabled={!!busy}
                onClick={() => setStatus("withdrawn")}
              >
                <X className="w-3.5 h-3.5 mr-1" />
                {busy === "withdrawn" ? "Withdrawing…" : "Withdraw"}
              </Button>
            )}
            <Button size="sm" onClick={save} disabled={saving || !!busy}>
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <Save className="w-3.5 h-3.5 mr-1" />
              )}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── QuotationRow — single quotation with attach actions ──────────────────────

function AttachButton({ quotId, type, label, current, onUpdated }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const updated = await v2Api.quotations.attach(quotId, type, file);
      toast.success(`${label} attached`);
      onUpdated(updated);
    } catch {
      toast.error(`Failed to attach ${label}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={handleFile}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border transition-colors ${
          current
            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
        }`}
        title={current ? `Replace ${label}` : `Attach ${label}`}
      >
        {uploading
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <Paperclip className="w-3 h-3" />}
        {current ? label : `+ ${label}`}
      </button>
    </>
  );
}

function StatusStepper({ status }) {
  const isSideBranch = ["rejected", "withdrawn", "expired"].includes(status);
  const currentIdx = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-0 mt-2">
      {STATUS_STEPS.map((step, idx) => {
        const isActive = step === status;
        const isDone = !isSideBranch && currentIdx > idx;
        const Icon = STATUS_ICONS[step] || FileText;
        return (
          <React.Fragment key={step}>
            <div className={`flex flex-col items-center ${idx === 0 ? "" : ""}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${
                isActive
                  ? `${STATUS_CLS[step]} border-current`
                  : isDone
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-slate-300 border-slate-200"
              }`}>
                {isDone
                  ? <Check className="w-3 h-3" />
                  : <Icon className="w-3 h-3" />}
              </div>
              <span className={`text-[9px] mt-0.5 whitespace-nowrap ${
                isActive ? "font-semibold text-slate-700" : isDone ? "text-emerald-600" : "text-slate-400"
              }`}>
                {STATUS_LABELS[step]}
              </span>
            </div>
            {idx < STATUS_STEPS.length - 1 && (
              <div className={`h-px flex-1 mb-3 mx-0.5 ${isDone ? "bg-emerald-300" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        );
      })}
      {isSideBranch && (
        <div className="ml-3 flex items-center gap-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center border ${STATUS_CLS[status]} border-current`}>
            {React.createElement(STATUS_ICONS[status] || X, { className: "w-3 h-3" })}
          </div>
          <span className="text-[9px] font-semibold text-slate-700">{STATUS_LABELS[status]}</span>
        </div>
      )}
    </div>
  );
}

function QuotationRow({ q, onOpen, onUpdated }) {
  const [busy, setBusy] = React.useState(null);

  const quickSendForReview = async (e) => {
    e.stopPropagation();
    setBusy("reviewing");
    try {
      const updated = await v2Api.quotations.sendForReview(q.id);
      toast.success("Sent for review — approval chain initiated");
      onUpdated(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? err?.message ?? "Could not send for review");
    } finally {
      setBusy(null);
    }
  };

  const quickSetStatus = async (e, status) => {
    e.stopPropagation();
    setBusy(status);
    try {
      const updated = await v2Api.quotations.setStatus(q.id, status);
      toast.success(`Quotation marked as ${status.replace(/_/g, " ")}`);
      onUpdated(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? err?.message ?? "Status update failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="border rounded-lg overflow-hidden hover:border-blue-200 transition">
      {/* Main row — click to open detail dialog */}
      <div
        role="button"
        tabIndex={0}
        className="flex items-center justify-between gap-2 p-3 cursor-pointer hover:bg-slate-50 select-none"
        onClick={onOpen}
        onKeyDown={(e) => e.key === "Enter" && onOpen()}
      >
        <div className="min-w-0 flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm">{q.quotation_no}</p>
          <span className="text-xs text-slate-400">v{q.version}</span>
          <span className="text-[10px] text-slate-400">
            {new Date(q.created_at).toLocaleDateString("en-IN")}
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <p className="font-mono font-semibold text-blue-700 text-sm">
            {moneyFmt(q.grand_total, q.currency)}
          </p>
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
      </div>

      {/* Status stepper */}
      <div className="px-3 pb-2">
        <StatusStepper status={q.status} />
      </div>

      {/* Actions strip */}
      <div
        className="flex items-center gap-2 flex-wrap px-3 py-2 bg-slate-50 border-t border-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Attach docs — only allowed in draft */}
        {q.status === "draft" && (
          <>
            <AttachButton quotId={q.id} type="proposal" label="Proposal" current={!!q.proposal_path} onUpdated={onUpdated} />
            <AttachButton quotId={q.id} type="cover_letter" label="Cover Letter" current={!!q.cover_letter_path} onUpdated={onUpdated} />
          </>
        )}

        {/* Download attached docs */}
        {q.proposal_path && (
          <a href={v2Api.quotations.downloadUrl(q.id, "proposal")} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
            <Download className="w-3 h-3" /> {q.proposal_filename || "Proposal"}
          </a>
        )}
        {q.cover_letter_path && (
          <a href={v2Api.quotations.downloadUrl(q.id, "cover_letter")} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline">
            <Download className="w-3 h-3" /> {q.cover_letter_filename || "Cover Letter"}
          </a>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Quick status actions */}
        {["draft", "rejected"].includes(q.status) && (
          <button
            type="button"
            disabled={!!busy}
            onClick={quickSendForReview}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 transition disabled:opacity-50"
          >
            {busy === "reviewing"
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <ShieldCheck className="w-3 h-3" />}
            {busy === "reviewing" ? "Sending…" : "Send for Review"}
          </button>
        )}
        {q.status === "approved" && (
          <button
            type="button"
            disabled={!!busy}
            onClick={(e) => quickSetStatus(e, "sent")}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition disabled:opacity-50"
          >
            {busy === "sent" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {busy === "sent" ? "Marking…" : "Mark as Sent"}
          </button>
        )}
      </div>
    </li>
  );
}

// ── QuotationPanel — list view ────────────────────────────────────────────────

export default function QuotationPanel({ bid, refreshKey = 0 }) {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);

  const refresh = async () => {
    if (!bid?.id) return;
    try {
      const items = await v2Api.quotations.listForBid(bid.id);
      setList(items);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid?.id, refreshKey]);

  const handleUpdated = (updated) => {
    setList((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    setSelected(updated);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" /> Quotations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {list.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No quotations yet. Save BOQ pricing then click "Generate quotation".
            </p>
          ) : (
            <ul className="space-y-2">
              {list.map((q) => (
                <QuotationRow
                  key={q.id}
                  q={q}
                  onOpen={() => setSelected(q)}
                  onUpdated={(updated) =>
                    setList((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                  }
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected && (
        <QuotationEditorDialog
          quotation={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </>
  );
}
